import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { Toaster as Sonner, toast } from 'sonner';
import { t } from '@ticarishift/i18n';

/**
 * Bildirim alanı (08 §6 #35, E-05): toast'lar `aria-live` bölgesinde duyurulur. Uygulama kökünde bir kez. Altta sabit
 * sekme çubuğu varsa toast'lar onun üstünde açılır (`--toast-bottom-inset`, `base.css`).
 */
export function Toaster() {
  return (
    <Sonner
      position="bottom-right"
      offset={{ bottom: 'var(--toast-bottom-inset, 24px)' }}
      mobileOffset={{ bottom: 'var(--toast-bottom-inset, 16px)' }}
      containerAriaLabel={t('common.a11y.notifications')}
      closeButton
      gap={8}
      icons={{
        success: <CircleCheck aria-hidden size={18} strokeWidth={1.75} className="text-status-ok" />,
        error: <CircleAlert aria-hidden size={18} strokeWidth={1.75} className="text-status-danger" />,
        warning: <TriangleAlert aria-hidden size={18} strokeWidth={1.75} className="text-status-attention" />,
        info: <Info aria-hidden size={18} strokeWidth={1.75} className="text-status-info" />,
      }}
      toastOptions={{
        unstyled: true,
        closeButtonAriaLabel: t('common.a11y.closeNotification'),
        classNames: {
          toast:
            'z-toast flex w-full items-start gap-3 rounded-lg border bg-surface p-4 text-body text-fg shadow-lg md:w-[380px] dark:border-strong',
          title: 'font-medium',
          description: 'text-body-sm text-fg-muted',
          actionButton: 'ms-auto shrink-0 rounded-md px-2 py-1 text-body-sm font-medium text-link hover:bg-surface-muted',
          closeButton:
            'order-last ms-auto inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-fg-muted hover:bg-surface-muted',
        },
      }}
    />
  );
}

type NotifyOptions = {
  description?: string;
  /** "Geri al" gibi tek eylem (08 §7: geri alınabilir işlemde onay yerine 5 sn'lik geri alma). */
  action?: { label: string; onClick: () => void };
  /** Kişi eylemi seçene ya da kapatana kadar kalır (ör. "Yeni sürüm hazır · Yenile"). */
  persistent?: boolean;
};

/**
 * Toast yardımcıları. Başarı ve bilgi kendiliğinden kapanır; hata kalıcıdır ve kullanıcı kapatır
 * (08 §6 #35, E-05, UX-18).
 */
export const notify = {
  success(message: string, options: NotifyOptions = {}) {
    return toast.success(message, { ...options, duration: options.action ? 5000 : 4000 });
  },
  info(message: string, { persistent, ...options }: NotifyOptions = {}) {
    return toast.info(message, { ...options, duration: persistent ? Number.POSITIVE_INFINITY : 5000 });
  },
  warning(message: string, options: NotifyOptions = {}) {
    return toast.warning(message, { ...options, duration: 8000 });
  },
  error(message: string, options: NotifyOptions = {}) {
    return toast.error(message, { ...options, duration: Number.POSITIVE_INFINITY });
  },
  dismiss(id?: string | number) {
    toast.dismiss(id);
  },
};
